import { useEffect, useMemo, useRef, useState } from 'react';
import { AppState } from 'react-native';
import {
  Category,
  CategoryKey,
  Service,
  categories as seedCategories,
} from '../data/services';
import { Catalogue, fetchCatalogue } from '../services/catalogue';

/**
 * How recently a successful fetch counts as "still current".
 *
 * The refetch below runs when the app comes back to the front, and somebody
 * flicking to WhatsApp to read one message and back is the ordinary case -
 * doing that ten times in a minute should not be ten requests. A minute is
 * long enough to collapse those and short enough that a customer who put the
 * phone down and picked it up again gets today's prices.
 */
const REFETCH_AFTER_MS = 60_000;

/**
 * What the app is selling, from the platform.
 *
 * ---------------------------------------------------------------------------
 * IT STARTS EMPTY, AND IT USED TO START WITH THE BUNDLED SEED
 * ---------------------------------------------------------------------------
 * The note here read: *"The first value is the bundled seed... the screen
 * draws the catalogue it shipped with and swaps it for the live one the moment
 * that lands... That is only honest because the two agree on what matters."*
 *
 * They stopped agreeing. The platform sells fifteen services today - Brooming
 * & Mopping, Car Wash, Fridge Cleaning, Express Pre-Party - and the bundle
 * carries a different set entirely, every one of which is a DRAFT on the
 * server. So this was showing a catalogue of things nobody can buy, on every
 * launch, for as long as the first request took. Fast fingers could reach one,
 * and the checkout then failed on a key the server has never heard of: the
 * bundle's ids are not uuids.
 *
 * Empty and `loading` is the honest first frame. The screens already branch
 * on `loading` for their skeletons, so the cost the old note was avoiding -
 * "every one of them would need its own empty state" - is a cost that has
 * already been paid.
 *
 * The seed itself is NOT deleted, and neither is the grouping below it: the
 * artwork lookup still reads that file, and restoring this behaviour is one
 * line if a future catalogue and bundle ever agree again.
 *
 * ---------------------------------------------------------------------------
 * ONE FETCH, AT THE TOP
 * ---------------------------------------------------------------------------
 * Called once in `HomeScreen` and passed down. Not a context and not a fetch
 * per screen: the catalogue is a few kilobytes, it changes about as often as
 * prices do, and a hook that refetches per mount would hit the API every time
 * somebody opened a category sheet.
 */
export type UseCatalogue = Catalogue & {
  /** True until the first response - server or fallback - has landed. */
  loading: boolean;
  /**
   * Asks again, and resolves when the answer has landed.
   *
   * Needed at first only since the bundle stopped standing in for a failed
   * request: a customer with no signal used to keep a screen full of cards and
   * nothing to retry, and now they have an empty one and a way out of it.
   *
   * It returns a promise because pull-to-refresh needs one. A `RefreshControl`
   * keeps spinning until it is told to stop, and "stop" has to mean the
   * catalogue actually arrived - a fire-and-forget reload leaves the caller
   * guessing, and every guess is either a spinner that stops before the new
   * prices are on screen or one that never stops at all.
   *
   * It never rejects. A failed refresh leaves the catalogue exactly as it was,
   * which is the right outcome and not an error the puller can act on.
   */
  reload: () => Promise<void>;
  categoryFor: (key: CategoryKey) => Category;
  servicesIn: (key: CategoryKey) => Service[];
};

export function useCatalogue(): UseCatalogue {
  const [state, setState] = useState<Catalogue>(() => ({
    categories: [],
    services: [],
    byCategory: {},
    /*
     * `server` before anything has arrived, deliberately.
     *
     * `source` exists so that anything about to take money can refuse to
     * quote from the bundle. There is nothing here to quote from at all now,
     * and calling an empty catalogue `seed` would have callers guarding
     * against a fallback that no longer happens.
     */
    source: 'server',
  }));
  const [loading, setLoading] = useState(true);
  /* Bumped by `reload`; the effect below runs again on every change. */
  const [attempt, setAttempt] = useState(0);

  /*
   * Guards a `setState` after unmount, which React logs as a leak and which
   * here would be a response landing on a screen the customer has left.
   */
  const alive = useRef(true);

  /**
   * When a fetch last came back from the server, as opposed to failing.
   *
   * Drives the throttle on the refetch below, and starts at 0 so the first
   * foreground after launch is never skipped.
   */
  const lastServerFetch = useRef(0);

  /**
   * Whether anything has ever been shown.
   *
   * The difference between the first load and every one after it: an empty
   * catalogue is the honest first frame, and a catastrophic replacement for a
   * screen that already has one.
   */
  const hasCatalogue = useRef(false);

  /**
   * Callers of `reload` waiting to be told the answer has landed.
   *
   * The fetch lives in an effect keyed on `attempt`, so `reload` cannot await
   * it directly - it can only ask for another run. This is the way back: the
   * promise it hands out is resolved from inside that run, whichever way the
   * run ends.
   *
   * A ref rather than state, because resolving one must not schedule a render
   * - that would run the effect again and ask the server a second time for an
   * answer it has already given.
   */
  const waiting = useRef<Array<() => void>>([]);

  /* Whatever happened, anybody who pulled is told it is over. A spinner that
     never stops is worse than a refresh that found nothing. */
  const settleWaiters = () => {
    waiting.current.splice(0).forEach(resolve => resolve());
  };

  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();

    /*
     * Only when there is nothing to look at.
     *
     * `loading` is what the screens draw skeletons from, and the background
     * refetch below runs on a screen that is already full. Raising it there
     * would collapse the whole catalogue into grey blocks every time somebody
     * came back from checking a message - a refresh nobody asked for,
     * announcing itself. A silent replacement is the point: the customer sees
     * the new price, not the fetch that found it.
     */
    if (!hasCatalogue.current) setLoading(true);

    fetchCatalogue(controller.signal)
      .then(next => {
        if (!alive.current) return;

        if (next.source === 'server') {
          lastServerFetch.current = Date.now();
          hasCatalogue.current = true;
          setState(next);
        } else if (!hasCatalogue.current) {
          /*
           * A failed fetch answers with an EMPTY catalogue marked `seed` -
           * see `fetchCatalogue`. On the first load, showing that is right:
           * there is nothing else, and the screens have an empty state.
           *
           * On a refetch it would be a disaster. Somebody who backgrounds the
           * app on a train and brings it back in a tunnel would watch the
           * entire catalogue vanish, having done nothing wrong. Keeping what
           * is already on screen is both kinder and more accurate - those
           * prices were real a minute ago, and no newer ones arrived.
           */
          setState(next);
        }

        setLoading(false);
        settleWaiters();
      })
      /*
       * `fetchCatalogue` no longer answers a failure with the bundle - it
       * returns an empty catalogue and says so - so this still only fires on
       * an abort. `loading` goes false either way: a spinner that never stops
       * is the one outcome worse than an empty screen.
       */
      .catch(() => {
        if (alive.current) {
          setLoading(false);
        }
        settleWaiters();
      });

    return () => {
      alive.current = false;
      controller.abort();
    };
  }, [attempt]);

  /*
   * Unmount, and ONLY unmount, settles anybody still waiting.
   *
   * This cannot live in the cleanup above. That cleanup also runs when
   * `attempt` changes - which is exactly what `reload` does - so flushing
   * there resolved the promise a tick after it was handed out and before the
   * fetch it is waiting for had even started. The pull would end instantly,
   * every time, and look like a refresh that had worked.
   *
   * Here the dependency list is empty, so it runs once when the screen goes
   * away. Without it, a pull that outlived its screen would leave its promise
   * unsettled forever - the fetch is aborted and the `.catch` above is guarded
   * on `alive`, so nothing else would ever resolve it.
   */
  useEffect(() => () => settleWaiters(), []);

  /*
   * Ask again when the app comes back to the front.
   *
   * ---------------------------------------------------------------------------
   * WHY THIS IS NOT A PULL-TO-REFRESH
   * ---------------------------------------------------------------------------
   * The catalogue was fetched exactly once, at mount, and held for the life of
   * the process. So anything ops changed - a price, a new service, a
   * photograph - was invisible to every handset that already had the app open,
   * until somebody killed it and started again. Nobody does that, and nobody
   * should have to: a customer looking at a stale PRICE is a support call, and
   * they have no way of knowing they are looking at one.
   *
   * A pull-to-refresh would fix it for the people who think to pull. This
   * fixes it for everyone, because backgrounding and returning is something
   * every customer does without being asked.
   *
   * Two guards on it:
   *
   * - **The app has to have actually been in the background.** iOS reports
   *   `inactive` for a pulled-down notification shade, a control centre swipe,
   *   an incoming call banner - the customer never left the screen, and
   *   refetching on each would fire several times a minute.
   * - **{@link REFETCH_AFTER_MS} since the last good answer.** App-switching is
   *   rapid and repeated; the catalogue changes about as often as prices do.
   *
   * `state.source` and the refs above are what make this safe to run
   * unattended: a refetch that fails leaves the screen exactly as it was.
   *
   * ---------------------------------------------------------------------------
   * WHY A FLAG RATHER THAN COMPARING THE PREVIOUS STATE
   * ---------------------------------------------------------------------------
   * The obvious version - "previous was background or inactive, and now it is
   * active" - is wrong on iOS, which does not go straight back: leaving is
   * `active → inactive → background` and returning is
   * `background → inactive → active`. Comparing against the immediately
   * previous value therefore sees `inactive → active` on a real return, which
   * is the exact pair a notification shade also produces. The two are
   * indistinguishable one step apart and distinguishable over the whole
   * sequence, so the whole sequence is what this tracks.
   *
   * It also avoids reading `AppState.currentState` for the initial value,
   * which is not reliably a string - on Android it can be null before the app
   * has finished starting, and `null.match(...)` is a crash on the first
   * background of the session.
   */
  useEffect(() => {
    let wasBackgrounded = AppState.currentState === 'background';

    const subscription = AppState.addEventListener('change', next => {
      if (next === 'background') {
        wasBackgrounded = true;
        return;
      }
      /* `inactive` on its own is a shade or a banner, not a departure. */
      if (next !== 'active') return;
      if (!wasBackgrounded) return;

      wasBackgrounded = false;
      if (Date.now() - lastServerFetch.current < REFETCH_AFTER_MS) return;

      setAttempt(n => n + 1);
    });

    return () => subscription.remove();
  }, []);

  return useMemo(() => {
    const byKey = new Map(state.categories.map(item => [item.key, item]));

    return {
      ...state,
      loading,
      /*
       * Deliberately NOT throttled by `REFETCH_AFTER_MS`.
       *
       * That throttle exists for the automatic foreground refetch, which fires
       * on something the customer did not ask for. A pull is a request, and
       * somebody who just watched ops change a price and pulled to see it must
       * get the fetch - telling them "not yet" is indistinguishable from the
       * stale catalogue this whole mechanism exists to stop.
       */
      reload: () =>
        new Promise<void>(resolve => {
          waiting.current.push(resolve);
          setAttempt(n => n + 1);
        }),
      /*
       * A category the app has never heard of is now possible - the list comes
       * from the server - so this falls back to the first rather than
       * pretending a missing key cannot happen.
       */
      /*
       * The list starts empty until the first fetch lands, and bookings can
       * arrive before it does - `state.categories[0]` was then undefined and
       * Home crashed on `.label` at launch. The bundled seed is only a label
       * and icon to draw with until the real list is here, never shown as
       * the catalogue.
       */
      categoryFor: (key: CategoryKey) =>
        byKey.get(key) ??
        state.categories[0] ??
        seedCategories.find(item => item.key === key) ??
        seedCategories[0],
      /*
       * Read from the server's own grouping, not by filtering the flat list:
       * a service sits on a shelf AND under the trade above it, which one
       * `category` field per service cannot express.
       */
      servicesIn: (key: CategoryKey) => state.byCategory[key] ?? [],
    };
  }, [loading, state]);
}
